import { Component, Input, computed, signal } from '@angular/core';
import { UiBadgeComponent } from '../../shared/ui';
import { RecipeFacets } from '../../services/recipe/recipe-facets.models';

/**
 * Read-only badges for a recipe's classification facets (course, seasons,
 * dish types). Used on the recipe list cards and the detail header.
 *
 * Facets carry stable keys; labels are rendered from what the API embeds
 * ({key,label} for course/seasons) plus a `dishTypeLabels` map that resolves
 * the dish-type KEYS (list/detail return dish types as bare keys) to labels.
 * A key with no known label falls back to the key itself.
 */
@Component({
  selector: 'app-recipe-facet-badges',
  standalone: true,
  imports: [UiBadgeComponent],
  template: `
    @if (hasAny()) {
      <div class="flex flex-wrap items-center gap-1">
        @if (facets()?.course; as course) {
          <ui-badge variant="primary" size="sm">{{ course.label }}</ui-badge>
        }
        @for (season of facets()?.seasons ?? []; track season.key) {
          <ui-badge variant="info" size="sm" [outline]="true">{{ season.label }}</ui-badge>
        }
        @for (dishType of facets()?.dish_types ?? []; track dishType) {
          <ui-badge variant="neutral" size="sm" [outline]="true">{{ dishTypeLabel(dishType) }}</ui-badge>
        }
      </div>
    }
  `,
})
export class RecipeFacetBadgesComponent {
  private readonly _facets = signal<RecipeFacets | null | undefined>(null);
  private readonly _dishTypeLabels = signal<Map<string, string>>(new Map());

  @Input() set facetsValue(value: RecipeFacets | null | undefined) {
    this._facets.set(value);
  }

  /** Map of dish-type key → display label, from the facet catalogue. */
  @Input() set dishTypeLabels(value: Map<string, string> | null | undefined) {
    this._dishTypeLabels.set(value ?? new Map());
  }

  readonly facets = computed(() => this._facets());

  readonly hasAny = computed(() => {
    const f = this._facets();
    if (!f) return false;
    return !!f.course || (f.seasons?.length ?? 0) > 0 || (f.dish_types?.length ?? 0) > 0;
  });

  dishTypeLabel(key: string): string {
    return this._dishTypeLabels().get(key) ?? key;
  }
}
